export const normalizeNightNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue)
    ? Math.max(0, Math.trunc(parsedValue))
    : 0;
};

export const normalizeNightInputValue = (value) =>
  String(value ?? '').trim() === '' ? '' : normalizeNightNumber(value);

const parseCalendarDate = (value) => {
  if (!value) return null;

  const match = String(value).trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/
  );
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return {
    key: `${match[1]}-${match[2]}-${match[3]}`,
    timestamp,
  };
};

export const getNightDateKey = (value, index) => {
  const parsedDate = parseCalendarDate(value);
  if (parsedDate) return parsedDate.key;
  return value ? String(value) : `__row_${index}`;
};

const normalizeTimeForComparison = (value) => {
  const match = String(value ?? '').trim().match(
    /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!match) return -1;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] || 0);
  if (hours > 23 || minutes > 59 || seconds > 59) return -1;

  return (hours * 60 * 60) + (minutes * 60) + seconds;
};

// Regroupe les nuitées par date et repère la dernière ligne horaire du jour.
export const getNightDistributionMeta = (rows = []) => {
  const groups = new Map();
  const datedKeys = new Map();

  rows.forEach((row, index) => {
    const parsedDate = parseCalendarDate(row?.date_begin);
    const dateKey = getNightDateKey(row?.date_begin, index);
    const timeValue = normalizeTimeForComparison(row?.heure_begin);
    const currentGroup = groups.get(dateKey) || {
      lastIndex: index,
      lastTime: timeValue,
      totalNights: 0,
      hasExplicitNightNumber: false,
    };

    if (
      timeValue > currentGroup.lastTime ||
      (timeValue === currentGroup.lastTime && index > currentGroup.lastIndex)
    ) {
      currentGroup.lastIndex = index;
      currentGroup.lastTime = timeValue;
    }
    currentGroup.totalNights += normalizeNightNumber(row?.night_number);
    currentGroup.hasExplicitNightNumber =
      currentGroup.hasExplicitNightNumber ||
      row?._nightNumberSource === 'saved' ||
      row?._nightNumberSource === 'manual';
    groups.set(dateKey, currentGroup);

    if (parsedDate) {
      datedKeys.set(dateKey, parsedDate.timestamp);
    }
  });

  let lastGlobalDate = null;
  let lastGlobalTimestamp = Number.NEGATIVE_INFINITY;
  datedKeys.forEach((timestamp, dateKey) => {
    if (timestamp > lastGlobalTimestamp) {
      lastGlobalDate = dateKey;
      lastGlobalTimestamp = timestamp;
    }
  });

  return { groups, lastGlobalDate };
};

export const distributeNightNumbers = (rows = []) => {
  const { groups, lastGlobalDate } = getNightDistributionMeta(rows);

  return rows.map((row, index) => {
    const dateKey = getNightDateKey(row?.date_begin, index);
    const group = groups.get(dateKey);
    const isLastHourlyRow = group?.lastIndex === index;
    const isLastMissionDate = dateKey === lastGlobalDate;
    const shouldKeepGroupTotal =
      !isLastMissionDate || group?.hasExplicitNightNumber;

    return {
      ...row,
      night_number: isLastHourlyRow && shouldKeepGroupTotal
        ? group.totalNights
        : 0,
    };
  });
};

export const countNightsMission = (prevDateString, startDateString, endDateString) => {
  const startDate = parseCalendarDate(startDateString);
  const endDate = parseCalendarDate(endDateString);
  if (!startDate || !endDate || endDate.timestamp < startDate.timestamp) return 0;

  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const nightDiff = startDate.timestamp === endDate.timestamp
    ? 1
    : Math.round((endDate.timestamp - startDate.timestamp) / millisecondsPerDay);
  const previousDate = parseCalendarDate(prevDateString);
  const isContinuation = previousDate?.key === startDate.key;

  return Math.max(0, nightDiff - (isContinuation ? 1 : 0));
};
